import React, { useState, useEffect, useRef } from 'react';
import assetUrl from '../../utils/assetUrl';

const EveSpecs = () => {
  const [visibleRows, setVisibleRows] = useState({
    stage1: new Set(),
    stage2: new Set(),
    aurora: new Set(),
    vac: new Set()
  });
  const stage1TableRef = useRef(null);
  const stage2TableRef = useRef(null);
  const auroraTableRef = useRef(null);
  const vacTableRef = useRef(null);
  const auroraTextRef = useRef(null);
  const changeViewTextRef = useRef(null);
  const dragStartXRef = useRef(null);
  const dragActiveRef = useRef(false);
  const [specTitleVisible, setSpecTitleVisible] = useState(false);
  const [activeAuroraView, setActiveAuroraView] = useState('aurora');
  const [fadeAuroraTable, setFadeAuroraTable] = useState(false);
  const [underlineWidth, setUnderlineWidth] = useState(0);
  const [underlineLeft, setUnderlineLeft] = useState(0);

  useEffect(() => {
    const timers = [];

    const observerCallback = (entries, stageName) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          
          const delays = [0, 200, 400];
          delays.forEach((delay, index) => {
            const timer = setTimeout(() => {
              setVisibleRows(prev => ({
                ...prev,
                [stageName]: new Set([...prev[stageName], index])
              }));
            }, delay);
            timers.push(timer);
          });
        }
      });
    };

    const observer1Ref = stage1TableRef.current;
    const observer2Ref = stage2TableRef.current;
    const auroraRef = auroraTableRef.current;
    const vacRef = vacTableRef.current;

    const observer1 = new IntersectionObserver(
      (entries) => observerCallback(entries, 'stage1'),
      { threshold: 0.8 }
    );

    const observer2 = new IntersectionObserver(
      (entries) => observerCallback(entries, 'stage2'),
      { threshold: 0.8 }
    );

    const auroraObserver = new IntersectionObserver(
      (entries) => observerCallback(entries, 'aurora'),
      { threshold: 0.8 }
    );

    const vacObserver = new IntersectionObserver(
      (entries) => observerCallback(entries, 'vac'),
      { threshold: 0.8 }
    );

    if (observer1Ref) observer1.observe(observer1Ref);
    if (observer2Ref) observer2.observe(observer2Ref);
    if (auroraRef) auroraObserver.observe(auroraRef);
    if (vacRef) vacObserver.observe(vacRef);

    
    const overviewEl = document.getElementById('overview-title');
    let overviewObserver;
    if (overviewEl) {
      overviewObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        
        setSpecTitleVisible(!entry.isIntersecting);
      }, { threshold: 0 });
      overviewObserver.observe(overviewEl);
    }

    return () => {
      timers.forEach(timer => clearTimeout(timer));
      if (observer1Ref) observer1.unobserve(observer1Ref);
      if (observer2Ref) observer2.unobserve(observer2Ref);
      if (auroraRef) auroraObserver.unobserve(auroraRef);
      if (vacRef) vacObserver.unobserve(vacRef);
      if (overviewObserver && overviewEl) overviewObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const updateUnderlinePosition = () => {
      const activeRef = activeAuroraView === 'aurora' ? auroraTextRef : changeViewTextRef;
      if (activeRef.current) {
        const textWidth = activeRef.current.offsetWidth;
        const extendedWidth = textWidth * 1.1;
        const extraWidth = extendedWidth - textWidth;
        setUnderlineWidth(extendedWidth);
        setUnderlineLeft(activeRef.current.offsetLeft - extraWidth / 2);
      }
    };

    updateUnderlinePosition();
    window.addEventListener('resize', updateUnderlinePosition);
    return () => window.removeEventListener('resize', updateUnderlinePosition);
  }, [activeAuroraView]);

  useEffect(() => {
    if (activeAuroraView === 'changeView') {
      setVisibleRows(prev => ({
        ...prev,
        vac: new Set([0, 1, 2])
      }));
    }
  }, [activeAuroraView]);

  const switchAuroraView = (nextView) => {
    if (nextView === activeAuroraView) return;
    setFadeAuroraTable(true);
    setTimeout(() => {
      setActiveAuroraView(nextView);
      setFadeAuroraTable(false);
    }, 200);
  };

  const handleDragStart = (clientX) => {
    dragStartXRef.current = clientX;
    dragActiveRef.current = true;
  };

  const handleDragEnd = (clientX) => {
    if (!dragActiveRef.current || dragStartXRef.current === null) {
      return;
    }

    const deltaX = clientX - dragStartXRef.current;
    const threshold = 40;

    if (Math.abs(deltaX) >= threshold) {
      const nextView = deltaX < 0 ? 'changeView' : 'aurora';
      switchAuroraView(nextView);
    }

    dragActiveRef.current = false;
    dragStartXRef.current = null;
  };
  return (
    <section style={{
      padding: '1px 20px 50px 20px',
      backgroundColor: '#000',
      color: '#fff',
      overflow: 'hidden'
    }}>
      <h2
        className="eve-specs-title"
        style={{
        fontSize: 'clamp(2rem, 5vw, 4rem)',
        textAlign: 'center',
        marginBottom: '80px',
        fontWeight: '700',
        letterSpacing: '0.1em',
        opacity: specTitleVisible ? 1 : 0,
        transition: 'opacity 0.4s ease-in-out'
      }}>
        SPECIFICATIONS
      </h2>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '100px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '60px',
          flexWrap: 'wrap',
          marginTop: '-80px'
        }}>
          <div style={{ flex: '1', minWidth: '300px', position: 'relative', zIndex: 2 }}>
            <h3 style={{
              fontSize: '2rem',
              marginBottom: '20px',
              fontWeight: '700'
            }}>FIRST STAGE</h3>
            <p style={{
              fontSize: '1.1rem',
              lineHeight: '1.6',
              opacity: 0.9,
              marginBottom: '30px'
            }}>
              Eve’s first stage features a quasi-monolithic aluminum orthogrid structure, powered by liquid methane and liquid oxygen. Twelve Aurora engines generate 143,000 pounds of thrust at sea level, supplied by an onboard 80 kWh battery system delivering up to 1.2 megawatts of electrical power to the first stage engines. The vehicle is configured for return-to-launch-site landings.
            </p>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: 'transparent'
            }} ref={stage1TableRef}>
              <tbody>
                <tr style={{
                  borderBottom: 'none',
                  backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                  backgroundPosition: 'bottom',
                  backgroundSize: '100% 1px',
                  backgroundRepeat: 'no-repeat',
                  opacity: visibleRows.stage1.has(0) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in'
                }}>
                  <td style={{
                    padding: '15px',
                    fontWeight: '600'
                  }}>NUMBER OF ENGINES</td>
                  <td style={{ padding: '15px' }}>12 Aurora</td>
                </tr>
                <tr style={{
                  borderBottom: 'none',
                  backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                  backgroundPosition: 'bottom',
                  backgroundSize: '100% 1px',
                  backgroundRepeat: 'no-repeat',
                  opacity: visibleRows.stage1.has(1) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in'
                }}>
                  <td style={{
                    padding: '15px',
                    fontWeight: '600'
                  }}>BURN TIME</td>
                  <td style={{ padding: '15px' }}>280 seconds</td>
                </tr>
                <tr style={{
                  borderBottom: 'none',
                  backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                  backgroundPosition: 'bottom',
                  backgroundSize: '100% 1px',
                  backgroundRepeat: 'no-repeat',
                  opacity: visibleRows.stage1.has(2) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in'
                }}>
                  <td style={{
                    padding: '15px',
                    fontWeight: '600'
                  }}>THRUST</td>
                  <td style={{ padding: '15px' }}>636 kN / 143,000 lbf</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ flex: '1', minWidth: '300px', overflow: 'hidden' }}>
            <img
              src={assetUrl('first-stage-v3.png')}
              alt="First Stage"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                transform: 'scale(.9)',
                transformOrigin: 'center'
              }}
            />
          </div>
        </div>

        {}
        <div style={{
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          gap: '60px',
          flexWrap: 'wrap',
          marginTop: '-60px'
        }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h3 style={{
              fontSize: '2rem',
              marginBottom: '20px',
              fontWeight: '700'
            }}>SECOND STAGE</h3>
            <p style={{
              fontSize: '1.1rem',
              lineHeight: '1.6',
              opacity: 0.9,
              marginBottom: '30px'
            }}>
              The second-stage orbiter is powered by three high-efficiency Aurora vacuum (A-VAC) engines. The system supports multiple in-space restarts, enabling payload delivery to multiple target orbits within a single mission profile. Following mission completion, the stage executes controlled atmospheric return using magnetohydrodynamic reentry (MHDR), followed by vertical landing and recovery.
            </p>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: 'transparent'
            }} ref={stage2TableRef}>
              <tbody>
                <tr style={{
                  borderBottom: 'none',
                  backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                  backgroundPosition: 'bottom',
                  backgroundSize: '100% 1px',
                  backgroundRepeat: 'no-repeat',
                  opacity: visibleRows.stage2.has(0) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in'
                }}>
                  <td style={{
                    padding: '15px',
                    fontWeight: '600'
                  }}>NUMBER OF ENGINES</td>
                  <td style={{ padding: '15px' }}>3 A-VAC</td>
                </tr>
                <tr style={{
                  borderBottom: 'none',
                  backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                  backgroundPosition: 'bottom',
                  backgroundSize: '100% 1px',
                  backgroundRepeat: 'no-repeat',
                  opacity: visibleRows.stage2.has(1) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in'
                }}>
                  <td style={{
                    padding: '15px',
                    fontWeight: '600'
                  }}>BURN TIME</td>
                  <td style={{ padding: '15px' }}>300 seconds</td>
                </tr>
                <tr style={{
                  borderBottom: 'none',
                  backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                  backgroundPosition: 'bottom',
                  backgroundSize: '100% 1px',
                  backgroundRepeat: 'no-repeat',
                  opacity: visibleRows.stage2.has(2) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in'
                }}>
                  <td style={{
                    padding: '15px',
                    fontWeight: '600'
                  }}>THRUST</td>
                  <td style={{ padding: '15px' }}>185 kN (vac) / 41,500 lbf (vac)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <img
              src={assetUrl('Stage 2.PNG')}
              alt="Second Stage"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </div>
        </div>

        {}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '60px',
          flexWrap: 'wrap',
          marginTop: '-60px'
        }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <div style={{ position: 'relative', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '40px', position: 'relative' }}>
                <h3
                  ref={auroraTextRef}
                  style={{
                    fontSize: activeAuroraView === 'aurora' ? '2rem' : '1.8rem',
                    margin: '0',
                    fontWeight: '700',
                    opacity: activeAuroraView === 'aurora' ? 1 : 0.66,
                    transition: 'all 0.4s ease-in-out',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={() => {
                    setFadeAuroraTable(true);
                    setTimeout(() => {
                      setActiveAuroraView('aurora');
                      setFadeAuroraTable(false);
                    }, 200);
                  }}
                >AURORA</h3>
                <h3
                  ref={changeViewTextRef}
                  style={{
                    fontSize: activeAuroraView === 'changeView' ? '2rem' : '1.8rem',
                    margin: '0',
                    fontWeight: '700',
                    opacity: activeAuroraView === 'changeView' ? 1 : 0.66,
                    transition: 'all 0.4s ease-in-out',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={() => {
                    setFadeAuroraTable(true);
                    setTimeout(() => {
                      setActiveAuroraView('changeView');
                      setFadeAuroraTable(false);
                    }, 200);
                  }}
                >A-VAC</h3>
              </div>
              <div style={{
                position: 'absolute',
                bottom: '-5px',
                left: `${underlineLeft}px`,
                width: `${underlineWidth}px`,
                height: '1px',
                background: 'linear-gradient(to right, transparent, white 10%, white 90%, transparent)',
                transition: 'all 0.4s ease-in-out'
              }} />
            </div>
            <p style={{
              fontSize: '1.1rem',
              lineHeight: '1.6',
              opacity: 0.9,
              marginBottom: '30px'
            }}>
              {activeAuroraView === 'aurora' 
                ? 'Aurora is a family of electric pump-cycle methalox rocket engines developed for use on the Eve launch vehicle. Aurora engines employ liquid oxygen and liquid methane propellants, driven by electrically powered turbomachinery to deliver precise thrust control and simplified engine startup. Designed from inception for high-cycle reuse, Aurora operates as a common engine architecture across multiple flight regimes, supporting both first, and upper-stage propulsion on Eve.'
                : 'Aurora vacuum extends the Aurora engine family as a vacuum-optimized second-stage configuration. A high expansion-ratio nozzle enables near-isentropic exhaust expansion in low ambient pressure, increasing effective exhaust velocity and vacuum specific impulse. This higher expansion ratio would cause flow separation and performance losses at sea level, making the vacuum variant essential for maximizing orbital payload delivery.'}
            </p>
            <div style={{
              opacity: fadeAuroraTable ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out'
            }}>
              {activeAuroraView === 'aurora' ? (
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: 'transparent'
                }} ref={auroraTableRef}>
                  <tbody>
                    <tr style={{
                      borderBottom: 'none',
                      backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                      backgroundPosition: 'bottom',
                      backgroundSize: '100% 1px',
                      backgroundRepeat: 'no-repeat',
                      opacity: visibleRows.aurora.has(0) ? 1 : 0,
                      transition: 'opacity 0.5s ease-in'
                    }}>
                      <td style={{
                        padding: '15px',
                        fontWeight: '600'
                      }}>Thrust</td>
                      <td style={{ padding: '15px' }}>53 kN / 12,000 lbf</td>
                    </tr>
                    <tr style={{
                      borderBottom: 'none',
                      backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                      backgroundPosition: 'bottom',
                      backgroundSize: '100% 1px',
                      backgroundRepeat: 'no-repeat',
                      opacity: visibleRows.aurora.has(1) ? 1 : 0,
                      transition: 'opacity 0.5s ease-in'
                    }}>
                      <td style={{
                        padding: '15px',
                        fontWeight: '600'
                      }}>Fuel</td>
                      <td style={{ padding: '15px' }}>LOX / CH4</td>
                    </tr>
                    <tr style={{
                      borderBottom: 'none',
                      backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                      backgroundPosition: 'bottom',
                      backgroundSize: '100% 1px',
                      backgroundRepeat: 'no-repeat',
                      opacity: visibleRows.aurora.has(2) ? 1 : 0,
                      transition: 'opacity 0.5s ease-in'
                    }}>
                      <td style={{
                        padding: '15px',
                        fontWeight: '600'
                      }}>ISP (Sea Level)</td>
                      <td style={{ padding: '15px' }}>315 seconds</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: 'transparent'
                }} ref={vacTableRef}>
                  <tbody>
                    <tr style={{
                      borderBottom: 'none',
                      backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                      backgroundPosition: 'bottom',
                      backgroundSize: '100% 1px',
                      backgroundRepeat: 'no-repeat',
                      opacity: visibleRows.vac.has(0) ? 1 : 0,
                      transition: 'opacity 0.5s ease-in'
                    }}>
                      <td style={{
                        padding: '15px',
                        fontWeight: '600'
                      }}>Thrust (Vac)</td>
                      <td style={{ padding: '15px' }}>53 kN / 12,000 lbf</td>
                    </tr>
                    <tr style={{
                      borderBottom: 'none',
                      backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                      backgroundPosition: 'bottom',
                      backgroundSize: '100% 1px',
                      backgroundRepeat: 'no-repeat',
                      opacity: visibleRows.vac.has(1) ? 1 : 0,
                      transition: 'opacity 0.5s ease-in'
                    }}>
                      <td style={{
                        padding: '15px',
                        fontWeight: '600'
                      }}>Fuel</td>
                      <td style={{ padding: '15px' }}>LOX / CH4</td>
                    </tr>
                    <tr style={{
                      borderBottom: 'none',
                      backgroundImage: 'linear-gradient(to right, transparent, white 5%, white 95%, transparent)',
                      backgroundPosition: 'bottom',
                      backgroundSize: '100% 1px',
                      backgroundRepeat: 'no-repeat',
                      opacity: visibleRows.vac.has(2) ? 1 : 0,
                      transition: 'opacity 0.5s ease-in'
                    }}>
                      <td style={{
                        padding: '15px',
                        fontWeight: '600'
                      }}>ISP (vacuum)</td>
                      <td style={{ padding: '15px' }}>380 seconds</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div
            className="eve-engine-image-wrap"
            style={{ flex: '1', minWidth: '300px', position: 'relative', height: '800px', overflow: 'visible', zIndex: 1, touchAction: 'pan-y' }}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture?.(event.pointerId);
              handleDragStart(event.clientX);
            }}
            onPointerUp={(event) => {
              handleDragEnd(event.clientX);
              event.currentTarget.releasePointerCapture?.(event.pointerId);
            }}
            onPointerCancel={(event) => {
              handleDragEnd(event.clientX);
              event.currentTarget.releasePointerCapture?.(event.pointerId);
            }}
          >
            <img
              src={assetUrl('Engine 3-4.PNG')}
              alt="Aurora Engine"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                position: 'absolute',
                top: '50%',
                left: activeAuroraView === 'aurora' ? '50%' : 'calc(50% - 200px)',
                transform: `translate(-50%, -50%) scale(${activeAuroraView === 'aurora' ? 1 : 0.85})`,
                filter: activeAuroraView === 'aurora' ? 'blur(0px) brightness(1)' : 'blur(8px) brightness(0.35)',
                transition: 'all 0.5s ease-in-out',
                zIndex: activeAuroraView === 'aurora' ? 2 : 0,
                objectFit: 'contain'
              }}
            />
            <img
              src={assetUrl('vac engine.png')}
              alt="Engine Motor"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                position: 'absolute',
                top: '50%',
                left: activeAuroraView === 'changeView' ? '50%' : 'calc(50% + 200px)',
                transform: `translate(-50%, -50%) scale(${activeAuroraView === 'changeView' ? 1 : 0.85})`,
                filter: activeAuroraView === 'changeView' ? 'blur(0px) brightness(1)' : 'blur(8px) brightness(0.35)',
                transition: 'all 0.5s ease-in-out',
                zIndex: activeAuroraView === 'changeView' ? 2 : 0,
                objectFit: 'contain'
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default EveSpecs;